import { useState } from 'react';
import type { IframePoolItem } from '../api';
import { CardModal } from '../design-system';
import { PotentialHitItem } from './PotentialHitItem';
import { PotentialHitSkeleton } from './PotentialHitSkeleton';

export interface PotentialHitsProps {
  items: IframePoolItem[];
  loading?: boolean;
  /** Per-card hit rate in percents; when provided renders Figma subtitle. */
  hitRatePerCard?: number;
}

const SKELETON_COUNT = 10;

export function PotentialHits({ items, loading, hitRatePerCard }: PotentialHitsProps) {
  const [selectedItem, setSelectedItem] = useState<IframePoolItem | null>(null);

  // Hide the section entirely when there's nothing to show and we're not still waiting.
  if (!loading && items.length === 0) return null;

  return (
    <section
      data-co-component="PotentialHits"
      className="
        relative overflow-hidden [border-radius:var(--co-PotentialHits-borderRadius,28px)]
        shadow-[0_64px_34px_0_rgba(0,0,0,0.05)]
        text-white p-[15px]
      "
      // Design Constructor surface: `PotentialHits`. Knobs match the
      // PackInfoCard pattern — bg/border/blur/typography. Defaults
      // reproduce the previous hardcoded glass wash so unmodified
      // deployments stay pixel-identical.
      style={{
        backgroundImage:
          'linear-gradient(' +
          'var(--co-PotentialHits-bgDirection, to bottom), ' +
          'color-mix(in srgb, var(--co-PotentialHits-bgFrom, rgba(255,255,255,0.20)) ' +
          'calc(var(--co-PotentialHits-bgOpacity, 1) * 100%), transparent) 0%, ' +
          'color-mix(in srgb, var(--co-PotentialHits-bgTo, rgba(153,153,153,0.20)) ' +
          'calc(var(--co-PotentialHits-bgOpacity, 1) * 100%), transparent) 100%)',
        backdropFilter: 'blur(var(--co-PotentialHits-glassBlur, 27px))',
        WebkitBackdropFilter: 'blur(var(--co-PotentialHits-glassBlur, 27px))',
        borderStyle: 'solid',
        borderWidth: 'var(--co-PotentialHits-borderWidth, 1px)',
        borderColor: 'var(--co-PotentialHits-borderColor, rgba(171,174,179,0.24))',
        fontFamily: 'var(--co-PotentialHits-fontFamily), Inter, system-ui, sans-serif',
      }}
      aria-label="Potential Hits"
    >
      <header className="mb-4 flex flex-col gap-[6px]">
        <h2
          className="font-bold uppercase leading-[26px]"
          style={{
            color: 'var(--co-PotentialHits-labelColor, #ffffff)',
            fontSize: 'var(--co-PotentialHits-labelSize, 18px)',
          }}
        >
          Potential Hits
        </h2>
        {hitRatePerCard != null && Number.isFinite(hitRatePerCard) && (
          <p className="type-body opacity-80">
            Hit rate per card {formatPercent(hitRatePerCard)}%
          </p>
        )}
      </header>

      <div
        className="
          grid grid-cols-2 gap-3
          @[600px]:grid-cols-3
          @[900px]:grid-cols-4
          @[1200px]:grid-cols-5
        "
      >
        {items.length > 0
          ? items.map((item) => (
              <PotentialHitItem
                key={item.id}
                item={item}
                onClick={setSelectedItem}
              />
            ))
          : Array.from({ length: SKELETON_COUNT }).map((_, idx) => (
              <PotentialHitSkeleton key={idx} />
            ))}
      </div>

      <CardModal
        open={selectedItem !== null}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
        label="Potential Hit"
      />
    </section>
  );
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}
